import * as React from "react";
import clsx from "clsx";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { Table } from "../../newcomponents/table/table";
import { TableNewItemButton } from "../../newcomponents/table/components/tableNewButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { NameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { Domain } from "../../schema/types/domain/entity";
import { linksOf } from "../../services/cpln";
import { domainHelpers, DomainTableItem } from "../../mst/kinds/domain.helpers";
import { TableActionsDropdown } from "../../newcomponents/table/components/tableActionsDropdown";
import { DeleteModal } from "../../components/modals/deleteModal";
import { useColumnsWithTagColumns } from "../../newcomponents/table/columns/tagColumns/useColumnsWithTagColumns";

export const DomainList: React.FC = () => {
  const [selectedLinks, setSelectedLinks] = React.useState<string[]>([]);
  const [action, setAction] = React.useState<string>("");

  const { kind, id } = useTableKindId("domain", "domain-list");
  const qData = useTableItemQueryData<Domain>(kind, {});
  const columns = useColumnsWithTagColumns<DomainTableItem>(
    [
      NameDescriptionColumn(),
      {
        id: "domainStatusText",
        label: "Status",
        enableSort: true,
        serverSortProperty: "status.status",
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <span
              className={clsx("truncate pr-2", {
                "color-action": value === "Ready",
                "color-warning": value === "Warning",
              })}
            >
              {value}
            </span>
          );
        },
      },
      { id: "type", label: "Type" },
      { id: "mappedGVC", label: "GVC" },
      { id: "endpointCount", label: "Endpoints" },
      TagsColumn(),
      CreatedColumn(),
      LastModifiedColumn(),
    ],
    kind,
  );

  const domainTableItems: DomainTableItem[] = qData.visibleItems.map((item) => {
    const selfLink = linksOf(item).self!;
    const domainStatusText = domainHelpers.getDomainStatusText(item);
    const type = domainHelpers.getType(item);
    const mappedGVC = domainHelpers.getMappedGVC(item);
    const endpointCount = domainHelpers.getEndpointCount(item);
    return {
      ...item,
      selfLink: selfLink,
      domainStatusText: domainStatusText,
      type: type,
      mappedGVC: mappedGVC,
      endpointCount: endpointCount,
    };
  });

  return (
    <>
      <Table<DomainTableItem>
        title={"Domains"}
        data={domainTableItems}
        selectKey="selfLink"
        selectMode="multi"
        selections={selectedLinks}
        onSelectionsChange={setSelectedLinks}
        tableId={id}
        headerRenderer={() => {
          return (
            <>
              <TableNewItemButton kind={kind} />
              <TableActionsDropdown
                kind={kind}
                selectionLinks={selectedLinks}
                options={[
                  {
                    key: "delete",
                    label: "Delete",
                    disabled: selectedLinks.length < 1,
                    danger: true,
                    callback: () => setAction("delete"),
                  },
                ]}
              />
              <TableHeaderRefreshButton onClick={qData.refetch} />
              <CustomTableCPLNQuery
                tableId={id}
                kind={kind}
                onQuery={qData.setCplnQuery}
                isLoading={qData.isLoading}
              />
            </>
          );
        }}
        enableFilter
        isLoading={qData.isLoading}
        onSortChange={qData.setSort}
        onSearchChange={qData.setSearch}
        isLoadingMore={qData.isLoadingMore}
        hasMore={qData.hasMore}
        onLoadMore={qData.fetchNextPage}
        columns={columns}
      />
      {action === "delete" ? (
        <DeleteModal
          kind={kind}
          onCancel={() => setAction("")}
          onComplete={() => {
            setAction("");
            setSelectedLinks([]);
            qData.refetch();
          }}
          links={selectedLinks}
        />
      ) : null}
    </>
  );
};
